import { useEffect, useRef, useState } from "react";
import { useReportSchedules } from "../hooks/useReportSchedules";
import {
  TIMEZONE_OPTIONS,
  CRON_PRESETS,
  DEFAULT_CRON,
} from "../../admin/form-config/export/destination-options";
import { apiErrorCode } from "@/lib/api-client";

// Task 8 (S-2) — inline schedule form + status for one saved-report row.
// One schedule per report in the v1 UI (the API allows multiple via POST;
// this is a deliberate v1 simplification, not a bug — task-8-brief.md).
//
// Recipients chip-input copies the interaction (not the markup) from
// EmailSection.tsx's opsRecipients editor: add-on-Enter, EMAIL_RE
// validation, dedupe, cap, keep-with-invalid-state on error.
//
// Cron presets + timezone options share the same cadence-vocabulary source
// of truth as the admin export SchedulePanel — both import CRON_PRESETS/
// DEFAULT_CRON/TIMEZONE_OPTIONS from destination-options.ts.

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const MAX_RECIPIENTS = 20;

interface Props {
  reportId: string;
}

export function SchedulePanel({ reportId }: Props) {
  const { schedules, create, update, pause } = useReportSchedules(reportId);
  const existing = schedules[0] ?? null;

  const [cronExpression, setCronExpression] = useState(DEFAULT_CRON);
  const [timezone, setTimezone] = useState("UTC");
  const [recipients, setRecipients] = useState<string[]>([]);
  const [recipientInput, setRecipientInput] = useState("");
  const [recipientError, setRecipientError] = useState<string | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [pausing, setPausing] = useState(false);

  // Seed the form from the existing schedule exactly once — after that, the
  // operator's in-progress edits win even if `existing` changes (e.g. after
  // their own save round-trips a fresh object back).
  const seededRef = useRef(false);
  useEffect(() => {
    if (existing && !seededRef.current) {
      seededRef.current = true;
      setCronExpression(existing.cronExpression);
      setTimezone(existing.timezone);
      setRecipients(existing.recipients);
    }
  }, [existing]);

  function handleRecipientKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key !== "Enter") return;
    e.preventDefault();

    const trimmed = recipientInput.trim();
    if (!trimmed) return;

    if (!EMAIL_RE.test(trimmed)) {
      setRecipientError("Invalid email address");
      return;
    }
    if (recipients.includes(trimmed)) {
      setRecipientError("Already added");
      return;
    }
    if (recipients.length >= MAX_RECIPIENTS) {
      setRecipientError(`Maximum ${MAX_RECIPIENTS} recipients`);
      return;
    }

    setRecipients((prev) => [...prev, trimmed]);
    setRecipientInput("");
    setRecipientError(null);
  }

  function handleRemoveRecipient(email: string) {
    setRecipients((prev) => prev.filter((r) => r !== email));
  }

  async function handleSave() {
    if (recipientError) return;
    setSaveError(null);
    setSaving(true);
    try {
      if (existing) {
        await update(existing.id, { cronExpression, timezone, recipients });
      } else {
        await create(cronExpression, timezone, recipients);
      }
    } catch (err) {
      // Surface the API's cron/recipients validation error verbatim —
      // readable messages are the API's job (report-schedules/index.ts).
      setSaveError(apiErrorCode(err));
    } finally {
      setSaving(false);
    }
  }

  async function handlePauseToggle() {
    if (!existing || pausing) return;
    setPausing(true);
    try {
      await pause(existing.id, !existing.isActive);
    } finally {
      setPausing(false);
    }
  }

  return (
    <div className="rbp-schedule-panel">
      {existing && (
        <div className="rbp-schedule-status-row">
          <span
            className={
              existing.lastRunStatus === "dead"
                ? "rbp-schedule-status rbp-schedule-status-danger"
                : "rbp-schedule-status"
            }
            data-testid={`schedule-status-${existing.id}`}
          >
            {existing.lastRunStatus ?? "not yet run"}
            {existing.lastRunAt ? ` · last: ${new Date(existing.lastRunAt).toLocaleString()}` : ""}
          </span>
          {existing.lastRunStatus === "dead" && existing.lastRunError && (
            <span className="rbp-error-text rbp-schedule-status-error" title={existing.lastRunError}>
              {existing.lastRunError}
            </span>
          )}
          <button
            type="button"
            className="rbp-btn"
            data-testid="schedule-pause-btn"
            onClick={handlePauseToggle}
            disabled={pausing}
          >
            {existing.isActive ? "Pause" : "Resume"}
          </button>
        </div>
      )}

      <div className="rbp-schedule-row">
        <label className="rbp-label" htmlFor={`schedule-cron-${reportId}`}>
          Cron
        </label>
        <input
          id={`schedule-cron-${reportId}`}
          className="rbp-input"
          data-testid="schedule-cron-input"
          value={cronExpression}
          onChange={(e) => setCronExpression(e.target.value)}
          placeholder="0 17 * * *"
          spellCheck={false}
        />
        <div className="rbp-schedule-presets">
          {CRON_PRESETS.map((preset) => (
            <button
              key={preset.cron}
              type="button"
              className="rbp-btn rbp-schedule-preset-btn"
              onClick={() => setCronExpression(preset.cron)}
            >
              {preset.label}
            </button>
          ))}
        </div>
      </div>

      <div className="rbp-schedule-row">
        <label className="rbp-label" htmlFor={`schedule-timezone-${reportId}`}>
          Timezone
        </label>
        <select
          id={`schedule-timezone-${reportId}`}
          className="rbp-select"
          data-testid="schedule-timezone-select"
          value={timezone}
          onChange={(e) => setTimezone(e.target.value)}
        >
          {TIMEZONE_OPTIONS.map((tz) => (
            <option key={tz.value} value={tz.value}>
              {tz.label}
            </option>
          ))}
        </select>
      </div>

      <div className="rbp-schedule-row">
        <label className="rbp-label" htmlFor={`schedule-recipient-${reportId}`}>
          Recipients
        </label>
        {recipients.length > 0 && (
          <div className="rbp-schedule-chip-list">
            {recipients.map((r) => (
              <span key={r} className="rbp-schedule-chip">
                {r}
                <button
                  type="button"
                  className="rbp-schedule-chip-remove"
                  onClick={() => handleRemoveRecipient(r)}
                  aria-label={`Remove ${r}`}
                >
                  &times;
                </button>
              </span>
            ))}
          </div>
        )}
        <input
          id={`schedule-recipient-${reportId}`}
          className="rbp-input"
          data-testid="schedule-recipient-input"
          value={recipientInput}
          onChange={(e) => {
            setRecipientInput(e.target.value);
            setRecipientError(null);
          }}
          onKeyDown={handleRecipientKeyDown}
          placeholder="ops@example.com — press Enter to add"
        />
        {recipientError && <span className="rbp-error-text">{recipientError}</span>}
      </div>

      {saveError && <div className="rbp-error-text">{saveError}</div>}

      <button
        type="button"
        className="rbp-btn rbp-btn-primary"
        data-testid="schedule-save-btn"
        onClick={handleSave}
        disabled={saving}
      >
        {saving ? "Saving…" : existing ? "Update Schedule" : "Create Schedule"}
      </button>
    </div>
  );
}
